import React, { useMemo } from 'react';
import { LuUsers, LuTrendingUp, LuTrendingDown } from 'react-icons/lu';
import SafeIcon from '../../common/SafeIcon';

const TeamPerformanceTable = ({ opportunities, teams }) => {
  const teamStats = useMemo(() => {
    return teams.map(team => {
      const teamOpps = opportunities.filter(opp => opp.team_id === team.id);
      const wonOpps = teamOpps.filter(opp => opp.stage === 'Closed Won');
      const activeOpps = teamOpps.filter(
        opp => !['Closed Won', 'Closed Lost'].includes(opp.stage)
      );
      const closedOpps = teamOpps.filter(
        opp => ['Closed Won', 'Closed Lost'].includes(opp.stage)
      );

      const revenue = wonOpps.reduce(
        (sum, opp) => sum + (parseFloat(opp.amount) || 0), 
        0
      );

      const pipelineValue = activeOpps.reduce(
        (sum, opp) => sum + (parseFloat(opp.amount) || 0), 
        0
      );

      const winRate = closedOpps.length > 0 
        ? (wonOpps.length / closedOpps.length) * 100 
        : 0;

      return {
        id: team.id,
        name: team.name,
        revenue,
        pipelineValue,
        activeDeals: activeOpps.length,
        wonDeals: wonOpps.length,
        winRate
      };
    }).sort((a, b) => b.revenue - a.revenue);
  }, [opportunities, teams]);

  const formatCurrency = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <SafeIcon icon={LuUsers} className="h-5 w-5 text-purple-500 mr-2" />
          Team Performance
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 px-2 font-medium text-gray-700">Team</th>
              <th className="text-right py-2 px-2 font-medium text-gray-700">Revenue</th>
              <th className="text-right py-2 px-2 font-medium text-gray-700">Pipeline</th>
              <th className="text-right py-2 px-2 font-medium text-gray-700">Win Rate</th>
            </tr>
          </thead>
          <tbody>
            {teamStats.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center py-8 text-gray-500">
                  No team data available
                </td>
              </tr>
            ) : (
              teamStats.map((team, index) => (
                <tr 
                  key={team.id} 
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <td className="py-3 px-2">
                    <div className="flex items-center space-x-2">
                      <div className={`h-6 w-6 rounded-md flex items-center justify-center text-xs font-bold text-white ${
                        index === 0 ? 'bg-yellow-500' : 
                        index === 1 ? 'bg-gray-400' : 
                        index === 2 ? 'bg-orange-400' : 'bg-gray-300'
                      }`}>
                        {index + 1}
                      </div>
                      <span className="font-medium text-gray-900">{team.name}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 ml-8">
                      {team.activeDeals} active • {team.wonDeals} won
                    </div>
                  </td>
                  <td className="py-3 px-2 text-right">
                    <span className="font-semibold text-gray-900">
                      {formatCurrency(team.revenue)}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-right">
                    <span className="text-gray-700">
                      {formatCurrency(team.pipelineValue)}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-right">
                    <div className="flex items-center justify-end space-x-1">
                      {team.winRate >= 50 ? (
                        <SafeIcon icon={LuTrendingUp} className="h-3 w-3 text-green-600" />
                      ) : (
                        <SafeIcon icon={LuTrendingDown} className="h-3 w-3 text-red-600" />
                      )}
                      <span className={`font-medium ${
                        team.winRate >= 50 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {team.winRate.toFixed(0)}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TeamPerformanceTable;